import { z } from "zod";

export const CONTACT_FORM_NAME = "soul-shack-contact" as const;
export const NEWSLETTER_FORM_NAME = "soul-shack-newsletter" as const;
export const QUESTIONNAIRE_VERSION = "soul-check-v1" as const;

export const q1Codes = [
  "change_stirring",
  "ground_shifted",
  "letting_go_in_between",
  "needs_shared_support",
] as const;

export const q2Codes = [
  "self_guided_own_pace",
  "understood_without_fixing",
  "longer_shared_process",
] as const;

export const q3Codes = [
  "repeating_personal_patterns",
  "relationships",
  "work_purpose",
  "self_care_overextended",
  "soul_connection",
] as const;

export const q4Codes = [
  "solitude_own_pace",
  "support_soon",
  "ready_to_dive_together",
] as const;

export const recommendationCodes = [
  "special",
  "heart-to-heart",
  "the-wave",
] as const;

export const soulCheckAnswersSchema = z
  .object({
    q1: z.enum(q1Codes),
    q2: z.enum(q2Codes),
    q3: z.enum(q3Codes),
    q4: z.enum(q4Codes),
  })
  .strict();

export type SoulCheckAnswers = z.infer<typeof soulCheckAnswersSchema>;
export type Recommendation = (typeof recommendationCodes)[number];

/**
 * The honeypot: any value at all means a bot filled it. It has to tolerate the
 * key being absent as well as empty, because the Netlify Function strips empty
 * fields before validating and Netlify itself omits the honeypot entirely.
 * A bare union of "" and undefined still demands the key be present.
 */
const emptyOrMissing = z.literal("").optional();
const optionalShortText = (maximum: number) =>
  z.preprocess(
    (value) => (value === "" ? undefined : value),
    z.string().trim().max(maximum).optional(),
  );

const sharedFormFields = {
  first_name: optionalShortText(80),
  email: z.string().trim().toLowerCase().email().max(254),
  website: emptyOrMissing,
};

export const contactSubmissionSchema = z
  .object({
    "form-name": z.literal(CONTACT_FORM_NAME),
    ...sharedFormFields,
    contact_message: optionalShortText(800),
    contact_consent: z.literal("yes"),
    newsletter_consent: z.enum(["yes", "no"]).default("no"),
    share_soul_check: z.enum(["yes", "no"]).default("no"),
    questionnaire_version: optionalShortText(40),
    q1: z.enum(q1Codes).optional(),
    q2: z.enum(q2Codes).optional(),
    q3: z.enum(q3Codes).optional(),
    q4: z.enum(q4Codes).optional(),
    share_reflection: z.enum(["yes", "no"]).default("no"),
    reflection: optionalShortText(1_200),
  })
  .superRefine((submission, context) => {
    const answerKeys = ["q1", "q2", "q3", "q4"] as const;
    const suppliedAnswers = answerKeys.filter(
      (key) => submission[key] !== undefined,
    );

    if (submission.share_soul_check === "yes") {
      if (submission.questionnaire_version !== QUESTIONNAIRE_VERSION) {
        context.addIssue({
          code: "custom",
          path: ["questionnaire_version"],
          message: "Onbekende versie van de Soul Check.",
        });
      }

      for (const key of answerKeys) {
        if (submission[key] === undefined) {
          context.addIssue({
            code: "custom",
            path: [key],
            message: "Dit antwoord ontbreekt.",
          });
        }
      }
    } else if (suppliedAnswers.length > 0) {
      context.addIssue({
        code: "custom",
        path: ["share_soul_check"],
        message: "Soul Check-antwoorden mogen alleen na toestemming mee.",
      });
    }

    if (submission.share_reflection === "yes" && !submission.reflection) {
      context.addIssue({
        code: "custom",
        path: ["reflection"],
        message: "De gekozen reflectie ontbreekt.",
      });
    }

    if (submission.share_reflection === "no" && submission.reflection) {
      context.addIssue({
        code: "custom",
        path: ["share_reflection"],
        message: "Een reflectie mag alleen na toestemming mee.",
      });
    }
  });

export const newsletterSubmissionSchema = z.object({
  "form-name": z.literal(NEWSLETTER_FORM_NAME),
  ...sharedFormFields,
  newsletter_consent: z.literal("yes"),
});

export type ContactSubmission = z.infer<typeof contactSubmissionSchema>;
export type NewsletterSubmission = z.infer<typeof newsletterSubmissionSchema>;
export type VerifiedFormSubmission = ContactSubmission | NewsletterSubmission;

export function parseVerifiedFormSubmission(
  input: Record<string, unknown>,
) {
  if (input["form-name"] === CONTACT_FORM_NAME) {
    return contactSubmissionSchema.safeParse(input);
  }

  if (input["form-name"] === NEWSLETTER_FORM_NAME) {
    return newsletterSubmissionSchema.safeParse(input);
  }

  return z
    .object({ "form-name": z.never() })
    .safeParse({ "form-name": input["form-name"] });
}

export function getSharedSoulCheckAnswers(
  submission: ContactSubmission,
): SoulCheckAnswers | undefined {
  if (submission.share_soul_check !== "yes") {
    return undefined;
  }

  const parsed = soulCheckAnswersSchema.safeParse({
    q1: submission.q1,
    q2: submission.q2,
    q3: submission.q3,
    q4: submission.q4,
  });

  return parsed.success ? parsed.data : undefined;
}
